import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { ProgressBar } from "../components/ProgressBar";
import ComicsModal from "../components/ComicsModal";
import { api } from "../lib/api";
import { useT } from "../i18n";
import { ArrowRight, ShieldCheck, BookOpen, Headphones, Eye, Gavel, Sparkles, Languages as LangIcon, Clock, Users2, FileCheck, BookMarked } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/57565/pexels-photo-57565.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMPACT_IMG = "https://images.pexels.com/photos/18012464/pexels-photo-18012464.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
// Basavanna — 12th-century philosopher & social reformer. Karnataka heritage imagery honouring the movement's roots.
const BASAVANNA_IMG = "https://images.pexels.com/photos/32216134/pexels-photo-32216134.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BASAVANNA_FALLBACK = "https://images.pexels.com/photos/14741977/pexels-photo-14741977.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const { t } = useT();
  const [stats, setStats] = useState({ total_books_donated: 0, total_students_impacted: 0, total_amount_raised: 0, total_schools: 0 });
  const [schools, setSchools] = useState([]);
  const [activity, setActivity] = useState([]);
  const [comicsOpen, setComicsOpen] = useState(false);

  useEffect(() => {
    api.get("/public/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/public/schools").then(r => setSchools(r.data.slice(0, 4))).catch(() => {});
    api.get("/public/activity").then(r => setActivity(r.data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 opacity-[0.08] bg-center bg-cover" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="bb-paper-grain absolute inset-0 opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7 bb-fade-up">
            <div className="bb-overline text-[var(--bb-terracotta)] mb-6">{t("hero_overline")}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[var(--bb-forest)] tracking-tight">
              {t("hero_title_1")}<br/>
              <em className="text-[var(--bb-terracotta)] not-italic italic">{t("hero_title_em")}</em>
            </h1>

            <p className="mt-8 text-xl md:text-2xl font-serif italic text-[var(--bb-terracotta)] leading-snug max-w-xl" data-testid="hero-fomo">
              {t("hero_fomo")}
            </p>

            <p className="mt-6 text-base md:text-lg text-[var(--bb-ink)]/80 max-w-xl leading-relaxed whitespace-pre-line">
              {t("hero_body")}
            </p>

            {/* Vachana emphasis */}
            <div className="mt-8 max-w-lg border-l-2 border-[var(--bb-terracotta)] pl-5 py-1" data-testid="hero-vachana">
              <div className="font-serif text-2xl md:text-3xl text-[var(--bb-forest)] leading-snug">{t("hero_vachana")}</div>
              <div className="mt-1 text-xs md:text-sm text-[var(--bb-muted)] italic">{t("hero_vachana_en")}</div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/schools" className="bb-btn-primary inline-flex items-center gap-2" data-testid="hero-cta-browse">
                {t("hero_cta_browse")} <ArrowRight size={16} />
              </Link>
              <Link to="/about" className="bb-btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-how">
                {t("hero_cta_how")}
              </Link>
              <button onClick={() => setComicsOpen(true)} className="bb-btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-comics">
                <BookMarked size={14}/> {t("hero_cta_comics")}
              </button>
            </div>

            <div className="mt-12 grid grid-cols-3 max-w-lg gap-0 divide-x divide-[var(--bb-border)] border-y border-[var(--bb-border)] py-6">
              <Stat label={t("stat_books")} value={stats.total_books_donated.toLocaleString()} testid="stat-books" />
              <Stat label={t("stat_students")} value={stats.total_students_impacted.toLocaleString()} testid="stat-students" />
              <Stat label={t("stat_schools")} value={stats.total_schools.toLocaleString()} testid="stat-schools" />
            </div>
          </div>

          <div className="md:col-span-5 relative bb-fade-up bb-delay-2">
            <div className="aspect-[4/5] rounded-md overflow-hidden border border-[var(--bb-border)] shadow-[0_30px_60px_-30px_rgba(44,53,49,0.35)] relative">
              <img src={IMPACT_IMG} alt="Children reading" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -right-4 -top-6 w-40 h-40 rounded-md overflow-hidden border border-[var(--bb-border)] hidden md:block rotate-3 shadow-[0_20px_40px_-20px_rgba(44,53,49,0.5)]">
              <img
                alt="Basavanna — philosopher and social reformer"
                className="w-full h-full object-cover object-center"
                src={BASAVANNA_IMG}
                onError={(e) => { if (e.currentTarget.src !== BASAVANNA_FALLBACK) e.currentTarget.src = BASAVANNA_FALLBACK; }}
              />
              {/* subtle dark gradient overlay for text readability */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-black/10 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="home-value-prop">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="bb-overline text-[var(--bb-terracotta)] mb-4">{t("vp_overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[var(--bb-forest)] tracking-tight leading-[1.05]">
              {t("vp_title_1")} <em className="text-[var(--bb-terracotta)] not-italic italic">{t("vp_title_em")}</em> {t("vp_title_2")}
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-lg text-[var(--bb-ink)]/80 leading-relaxed max-w-2xl">{t("vp_body")}</p>
            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              <FormatTile icon={<BookOpen size={18}/>} title={t("vp_kannada")} body={t("vp_kannada_body")} />
              <FormatTile icon={<LangIcon size={18}/>} title={t("vp_english")} body={t("vp_english_body")} />
              <FormatTile icon={<Headphones size={18}/>} title={t("vp_audio")} body={t("vp_audio_body")} />
            </div>
            <p className="mt-8 text-sm text-[var(--bb-muted)] max-w-xl">{t("vp_closer")}</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[var(--bb-paper)] border-y border-[var(--bb-border)]" data-testid="home-how">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="bb-overline text-[var(--bb-terracotta)] mb-3">{t("how_overline")}</div>
          <h2 className="font-serif text-4xl md:text-5xl text-[var(--bb-forest)] tracking-tight mb-12 max-w-2xl">{t("how_title")}</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <HowStep n="01" title={t("how_1_title")} body={t("how_1_body")}/>
            <HowStep n="02" title={t("how_2_title")} body={t("how_2_body")}/>
            <HowStep n="03" title={t("how_3_title")} body={t("how_3_body")}/>
            <HowStep n="04" title={t("how_4_title")} body={t("how_4_body")}/>
          </div>
          <Link to="/about" className="bb-link text-sm mt-10 inline-block">{t("how_full")}</Link>
        </div>
      </section>

      {/* IMPACT DASHBOARD — 3 metrics only */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="home-impact">
        <div className="bb-overline text-[var(--bb-terracotta)] mb-3">{t("impact_overline")}</div>
        <h2 className="font-serif text-4xl md:text-5xl text-[var(--bb-forest)] tracking-tight mb-3 max-w-2xl">{t("impact_title")}</h2>
        <p className="text-sm text-[var(--bb-muted)] mb-10 max-w-xl">{t("impact_sub")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Metric label={t("impact_books")} value={stats.total_books_donated.toLocaleString()} testid="kpi-books-delivered"/>
          <Metric label={t("impact_children")} value={stats.total_students_impacted.toLocaleString()} testid="kpi-children-reached"/>
          <Metric label={t("impact_schools")} value={stats.total_schools.toLocaleString()} testid="kpi-schools-active"/>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-[var(--bb-paper)] border-y border-[var(--bb-border)]" data-testid="home-trust">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="bb-overline text-[var(--bb-terracotta)] mb-3">{t("trust_overline")}</div>
              <h2 className="font-serif text-4xl md:text-5xl text-[var(--bb-forest)] tracking-tight leading-tight">{t("trust_title")}</h2>
            </div>
            <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
              <TrustPoint icon={<ShieldCheck size={20}/>} title={t("trust_payments_title")} body={t("trust_payments_body")}/>
              <TrustPoint icon={<Eye size={20}/>} title={t("trust_proof_title")} body={t("trust_proof_body")}/>
              <TrustPoint icon={<Gavel size={20}/>} title={t("trust_mod_title")} body={t("trust_mod_body")}/>
              <TrustPoint icon={<Sparkles size={20}/>} title={t("trust_price_title")} body={t("trust_price_body")}/>
            </div>
          </div>
        </div>
      </section>

      {/* SCHOOLS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="home-schools">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="bb-overline text-[var(--bb-terracotta)] mb-3 flex items-center gap-2"><Clock size={12}/> {t("schools_overline_tag")}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[var(--bb-forest)] tracking-tight max-w-xl">{t("schools_title")}</h2>
            <p className="mt-3 text-sm text-[var(--bb-muted)] max-w-xl">{t("schools_body")}</p>
          </div>
          <Link to="/schools" className="bb-link text-sm hidden md:inline" data-testid="home-all-schools">{t("schools_all")}</Link>
        </div>

        {schools.length === 0 ? (
          <div className="bb-card p-10 text-center text-[var(--bb-muted)]">{t("schools_none")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {schools.map((s, i) => (
              <Link to={`/schools/${s.school_id}`} key={s.school_id} className="bb-card p-6 block bb-fade-up" style={{ animationDelay: `${0.1 * i}s` }} data-testid={`school-card-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="bb-tag bb-tag-green">{s.status}</div>
                    {s.mou_path && <div className="bb-tag" data-testid={`mou-badge-${i}`}><FileCheck size={10}/> {t("mou_badge")}</div>}
                  </div>
                  {s.logo_path && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${s.logo_path}`} alt="" className="w-10 h-10 rounded-full object-cover border border-[var(--bb-border)]"/>}
                </div>
                <div className="font-serif text-2xl text-[var(--bb-forest)] leading-tight mb-1 line-clamp-2">{s.name}</div>
                <div className="text-xs text-[var(--bb-muted)] mb-5 line-clamp-1">{s.address}</div>
                <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                  <div><div className="text-[var(--bb-muted)]">{t("impact_children")}</div><div className="text-[var(--bb-ink)] font-medium text-base">{s.total_students}</div></div>
                  <div><div className="text-[var(--bb-muted)]">{t("impact_books")}</div><div className="text-[var(--bb-ink)] font-medium text-base">{s.total_books_required}</div></div>
                </div>
                <ProgressBar value={s.total_books_funded || 0} total={s.total_books_required || 1} testId={`school-progress-${i}`} />
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* IMPACT FEED */}
      <section className="bg-[var(--bb-paper)] border-y border-[var(--bb-border)]" data-testid="home-feed">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="bb-overline text-[var(--bb-terracotta)] mb-3 flex items-center gap-2"><Users2 size={12}/> {t("feed_overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[var(--bb-forest)] tracking-tight mb-4">{t("feed_title")}</h2>
            <p className="text-[var(--bb-ink)]/75 leading-relaxed">{t("feed_body")}</p>
          </div>
          <div className="md:col-span-7">
            <div className="bb-card p-2">
              {activity.length === 0 ? (
                <div className="p-10 text-center text-[var(--bb-muted)] text-sm">{t("feed_empty")}</div>
              ) : (
                <ul className="divide-y divide-[var(--bb-border)]">
                  {activity.map((a, i) => (
                    <li key={a.donation_id || a.id || `${a.donor_name}-${a.donation_date}-${i}`} className="px-4 py-4 flex items-center gap-4" data-testid={`activity-${i}`}>
                      <div className="w-10 h-10 rounded-full bg-[var(--bb-terracotta)]/10 grid place-items-center text-[var(--bb-terracotta)]"><BookOpen size={16}/></div>
                      <div className="flex-1 text-sm">
                        <div><span className="font-medium">{a.donor_name}</span> · <b>{a.quantity}</b> sets · <span className="text-[var(--bb-forest)]">{a.school_name}</span></div>
                        {a.dedication && <div className="italic text-[var(--bb-muted)] text-xs mt-1">"{a.dedication}"</div>}
                      </div>
                      <div className="text-xs text-[var(--bb-muted)]">{a.donation_date?.slice(0, 10)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 my-20" data-testid="home-final-cta">
        <div className="relative bb-card p-10 md:p-20 overflow-hidden">
          <div className="absolute inset-0 bb-paper-grain opacity-50" />
          <div className="relative text-center max-w-3xl mx-auto">
            <div className="bb-overline text-[var(--bb-terracotta)] mb-6">{t("final_overline")}</div>
            <h2 className="font-serif text-4xl md:text-6xl text-[var(--bb-forest)] tracking-tight leading-[1.05]">
              {t("final_title_1")}<br/>
              <em className="text-[var(--bb-terracotta)] not-italic italic">{t("final_title_em")}</em>
            </h2>
            <p className="mt-8 text-[var(--bb-ink)]/75 leading-relaxed">{t("final_body")}</p>
            <div className="mt-10 flex justify-center gap-4 flex-wrap">
              <Link to="/schools" className="bb-btn-primary inline-flex items-center gap-2" data-testid="bottom-cta-donate">{t("final_cta_donate")}</Link>
              <Link to="/register-school" className="bb-btn-secondary inline-flex items-center gap-2" data-testid="bottom-cta-school">{t("final_cta_school")}</Link>
            </div>
          </div>
        </div>
      </section>
      {/* COMICS MODAL */}
      <ComicsModal open={comicsOpen} onClose={() => setComicsOpen(false)} />
    </PublicLayout>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="px-4 first:pl-0" data-testid={testid}>
      <div className="font-serif text-3xl text-[var(--bb-forest)]">{value}</div>
      <div className="bb-overline text-[var(--bb-muted)] mt-1">{label}</div>
    </div>
  );
}
function FormatTile({ icon, title, body }) {
  return (
    <div className="border border-[var(--bb-border)] rounded-md p-5 bg-[var(--bb-sand)]/50">
      <div className="w-9 h-9 rounded-full bg-[var(--bb-forest)]/8 text-[var(--bb-forest)] grid place-items-center mb-4">{icon}</div>
      <div className="font-serif text-xl text-[var(--bb-forest)] mb-1">{title}</div>
      <div className="text-xs text-[var(--bb-muted)] leading-relaxed">{body}</div>
    </div>
  );
}
function HowStep({ n, title, body }) {
  return (
    <div className="relative">
      <div className="font-serif text-5xl text-[var(--bb-terracotta)]/30 leading-none mb-3">{n}</div>
      <div className="font-serif text-2xl text-[var(--bb-forest)] mb-2 leading-tight">{title}</div>
      <div className="text-sm text-[var(--bb-muted)] leading-relaxed">{body}</div>
    </div>
  );
}
function Metric({ label, value, testid }) {
  return (
    <div className="bb-card p-8" data-testid={testid}>
      <div className="bb-overline text-[var(--bb-muted)] mb-3">{label}</div>
      <div className="font-serif text-5xl md:text-6xl text-[var(--bb-forest)]">{value}</div>
    </div>
  );
}
function TrustPoint({ icon, title, body }) {
  return (
    <div className="bb-card p-6">
      <div className="w-10 h-10 rounded-full bg-[var(--bb-terracotta)]/10 text-[var(--bb-terracotta)] grid place-items-center mb-3">{icon}</div>
      <div className="font-serif text-xl text-[var(--bb-forest)] mb-1 leading-tight">{title}</div>
      <div className="text-sm text-[var(--bb-muted)] leading-relaxed">{body}</div>
    </div>
  );
}
